import { useCallback, useEffect, useState } from "react";

import { upcomingFrom, type ScheduledSession } from "@/content/schedule";
import { scheduledSessions } from "@/content/schedule-data";
import { listScheduledSessions } from "@/utils/scheduler.functions";
import { supabase } from "@/integrations/supabase/client";

// Same course, start date and UTC start time (HH:MM) = same session.
export const keyOf = (s: ScheduledSession) =>
  `${s.courseSlug}|${s.startDate}|${(s.startTime ?? s.time).slice(0, 5)}`;

export interface ScheduleOverride {
  session_key: string;
  hidden: boolean;
  start_time: string | null;
  end_time: string | null;
  meeting_dates: string[] | null;
}

/** Spreadsheet sessions plus live Scheduler API sessions; the API copy wins on duplicates. */
export function mergeSessions(sheet: ScheduledSession[], live: ScheduledSession[]): ScheduledSession[] {
  const byKey = new Map<string, ScheduledSession>();
  for (const s of sheet) byKey.set(keyOf(s), s);
  for (const s of live) byKey.set(keyOf(s), s);
  return sortSessions([...byKey.values()]);
}

function sortSessions(list: ScheduledSession[]) {
  return list.sort(
    (a, b) =>
      a.startDate.localeCompare(b.startDate) || (a.startTime ?? "").localeCompare(b.startTime ?? ""),
  );
}

/** Apply staff edits from /admin/schedule: hide sessions or change their times and dates. */
export function applyOverrides(list: ScheduledSession[], overrides: ScheduleOverride[]) {
  const byKey = new Map(overrides.map((o) => [o.session_key, o]));
  const out: ScheduledSession[] = [];
  for (const s of list) {
    const o = byKey.get(keyOf(s));
    if (!o) {
      out.push(s);
      continue;
    }
    if (o.hidden) continue;
    const next = { ...s };
    if (o.start_time) next.startTime = o.start_time;
    if (o.end_time) next.endTime = o.end_time;
    if (o.start_time || o.end_time)
      next.time = `${next.startTime ?? ""} - ${next.endTime ?? ""} UTC`;
    if (o.meeting_dates?.length) {
      const dates = [...o.meeting_dates].sort();
      next.meetingDates = dates;
      next.startDate = dates[0];
      next.endDate = dates[dates.length - 1];
    }
    out.push(next);
  }
  return sortSessions(out);
}

let liveCache: ScheduledSession[] | null = null;
let overrideCache: ScheduleOverride[] | null = null;

export async function fetchOverrides(): Promise<ScheduleOverride[]> {
  const { data } = await supabase
    .from("schedule_overrides")
    .select("session_key,hidden,start_time,end_time,meeting_dates");
  overrideCache = (data as ScheduleOverride[] | null) ?? [];
  return overrideCache;
}

/** Spreadsheet + live API sessions, without staff edits applied. */
export function useRawSessions() {
  const [live, setLive] = useState<ScheduledSession[]>(liveCache ?? []);
  useEffect(() => {
    if (liveCache) return;
    let active = true;
    listScheduledSessions()
      .then((r) => {
        liveCache = r.sessions;
        if (active) setLive(r.sessions);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);
  return mergeSessions(scheduledSessions, live);
}

export function useOverrides() {
  const [overrides, setOverrides] = useState<ScheduleOverride[]>(overrideCache ?? []);
  const reload = useCallback(async () => setOverrides(await fetchOverrides()), []);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { overrides, reload };
}

/** Shows spreadsheet sessions immediately, then adds live API sessions and staff edits. */
export function useScheduledSessions() {
  const raw = useRawSessions();
  const { overrides } = useOverrides();
  const all = applyOverrides(raw, overrides);
  return { all, upcoming: upcomingFrom(all), loading: false };
}
